import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ModalContainerComponent} from '../modal-container/modal-container.component';
import {NgbAlert, NgbModal} from '@ng-bootstrap/ng-bootstrap';

import {UserService} from '@core/services/user.service';
import {LoadingComponent} from '../../general/loading/loading.component';
import {ErrorService} from '@core/services/error.service';
import {FormValidationClassDirective} from '@core/directives/form-validation-class.directive';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {CheckBadgeComponent} from '@core/svg-icons/shield-check-icon.component';

@Component({
  selector: 'app-create-password-modal',
  templateUrl: './create-password-modal.component.html',
  styleUrls: ['./create-password-modal.component.scss'],
  imports: [ModalContainerComponent, NgbAlert, LoadingComponent, FormValidationClassDirective, ReactiveFormsModule, CheckBadgeComponent]
})
export class CreatePasswordModalComponent implements OnInit {
  private modalService = inject(NgbModal);
  private userService = inject(UserService);
  private errorService = inject(ErrorService);


  message = signal<string | undefined>(undefined);
  email: string;
  phone: string;
  rpToken: string | undefined = undefined;
  fullname: string | undefined = undefined;

  showErrorsInForm = signal(false);
  loading = signal(false);
  invalidRPToken = signal(false);
  isCustomerCreateFlow = signal(false);

  resetPasswordForm = new FormGroup<{
    password: FormControl<string | null>,
  }>({
    password: new FormControl('', [Validators.required, Validators.minLength(8), this.validateUppercaseAndLowercase(), this.validateSpecialCharacterOrNumber()]),
  });

  title = computed(() => {
    if (this.isCustomerCreateFlow()) {
      return $localize`Opret kodeord`;
    }
    return $localize`Glemt kodeord`;
  });

  submitButtonText = computed(() => {
    if (this.isCustomerCreateFlow()) {
      return $localize`OPRET MIG`;
    }
    return $localize`GEM KODE`;
  });

  get passwordField() {
    return this.resetPasswordForm.controls.password;
  }

  ngOnInit() {
    this.submitRpToken();
  }

  validateUppercaseAndLowercase() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const hasUppercase = /[A-Z]/.test(control.value);
      const hasLowercase = /[a-z]/.test(control.value);
      if (!hasUppercase || !hasLowercase) {
        return {uppercaseAndLowercase: true};
      }
      return null;
    };
  }

  validateSpecialCharacterOrNumber() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const hasSpecialCharacter = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(control.value);
      const hasNumber = /[0-9]/.test(control.value);
      if (!hasSpecialCharacter && !hasNumber) {
        return {specialCharacterOrNumber: true};
      }
      return null;
    };
  }

  validMin8Letters() {
    if (!this.passwordField.value) {
      return false;
    }
    return !this.passwordField.getError('minlength');
  }

  validSmallAndBigLetters() {
    if (!this.passwordField.value) {
      return false;
    }
    return !this.passwordField.getError('uppercaseAndLowercase');
  }

  validSpecialCharacterOrNumber() {
    if (!this.passwordField.value) {
      return false;
    }
    return !this.passwordField.getError('specialCharacterOrNumber');
  }

  submitRpToken() {
    this.message.set(undefined);
    if (!this.rpToken) {
      return;
    }
    this.loading.set(true);
    this.userService.isResetPasswordTokenValid(this.email, this.rpToken).then(isValid => {
      if (!isValid) {
        this.invalidRPToken.set(true);
      }
    }).finally(() => {
      this.loading.set(false);
    }).catch(e => {
      this.userService.createPasswordLinkErrorModal(this.email, this.errorService.getErrorMessage(e));
    })
  }

  submitPassword() {
    this.resetPasswordForm.markAllAsTouched();
    this.showErrorsInForm.set(true);
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.message.set(undefined);
    const email = this.email;
    const phone = this.phone;
    const rpToken = this.rpToken?.trim();
    const fullname = this.fullname;
    const password = this.resetPasswordForm.controls.password.value;

    if (!password) {
      return;
    }

    this.loading.set(true);

    if (this.isCustomerCreateFlow()) {
      this.registerCustomer(email, fullname, password, phone);
    } else {
      this.changePassword(email, password, rpToken);
    }
  }

  registerCustomer(email: string, fullname: string | undefined, password: string, phone: string) {
    this.message.set(undefined);
    this.userService.registerCustomer(email, fullname, password, phone).then(() => {
      this.loading.set(false);
    }).catch(err => {
      this.message.set(this.errorService.getErrorMessage(err));
      this.loading.set(false);
    })
  }

  loginModal(email: string | null = null) {
    this.modalService.dismissAll();
    import('../login-modal/login-modal.component').then(({LoginModalComponent}) => {
      const modalReference = this.modalService.open(LoginModalComponent);
      if (email) {
        modalReference.componentInstance.email = email;
      }
    });
  }

  goToWelcomeModal() {
    this.modalService.dismissAll();
    import('../welcome-modal/welcome-modal.component').then(({WelcomeModalComponent}) => {
      const modalRef = this.modalService.open(WelcomeModalComponent);
      modalRef.componentInstance.message.set($localize`Perfekt! Din nye kode er nu gemt, og du er logget ind.`);
    });
  }

  protected invalidTokenErrorHandler(errorMessage: string) {
    if (errorMessage.indexOf('The password token is mismatched. Reset and try again') !== -1) {
      this.invalidRPToken.set(true);
    }
  }

  private changePassword(email: string, password: string, rpToken: string | null | undefined) {
    if (!rpToken) {
      return;
    }
    this.userService.changePassword(password, email, rpToken).catch(error => {
      this.loading.set(false);
      const message = error.message;
      this.message.set(this.errorService.getErrorMessage(error));
      this.invalidTokenErrorHandler(message);

    }).then(response => {
      if (response) {
        this.userService.login(email, password).then(() => {
          this.loading.set(false);
          this.goToWelcomeModal();
        }).catch(error => {
          this.loading.set(false);
          this.message.set(this.errorService.getErrorMessage(error));
        });
      }
    });
  }

}
